import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { HomingMissile } from './homingMissile';
import { WA_PHYSICS } from '../core/constants';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * Kalsiddon (GunBound mobile - Thor's Hammer). A bomb is lobbed in an arc and
 * SPLITS at the top of its arc into several homing missiles that then LOITER in
 * the air. The bomb keeps falling; the instant it hits something (terrain or a
 * worm) the loitering missiles are RELEASED and converge onto that exact
 * contact point, spiralling in a little. Apex is detected in Projectile.update
 * (vy crosses from rising to falling) via onApexSplit; both the split and the
 * release are authority-only (client-synced bombs have no callbacks, so they
 * never double-spawn).
 */
export const WeaponKalsiddon: WeaponDefinition = {
  id: 'kalsiddon',
  name: 'Kalsiddon',
  icon: 'kalsiddon.png',
  category: 'thrown',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, world } = ctx;

    const speed = (power / 100) * 750;
    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
    const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);

    const bomb = new Projectile({
      x: spawn.x,
      y: spawn.y,
      vx: Math.cos(aimRad) * speed,
      vy: -Math.sin(aimRad) * speed,
      type: 'kalsiddon',
      timer: -1,
      windAffected: true,
      ownerId: worm.id,
      // The bomb/tracer does no crater or contact damage - hitting just RELEASES
      // the loitering missiles (onExplode), which are the whole payload.
      noContactExplosion: true,
    });

    // At the apex, split into loitering missiles fanned out across SPREAD_X.
    // They hover until the bomb hits, then converge onto the contact point.
    const loiterers: HomingMissile[] = [];
    bomb.onApexSplit = (x, y) => {
      const { COUNT, SPREAD_X, SPREAD_VX } = WA_PHYSICS.KALSIDDON;
      const half = (COUNT - 1) / 2;
      for (let i = 0; i < COUNT; i++) {
        const f = COUNT > 1 ? (i - half) / half : 0; // -1..1
        const missile = new HomingMissile({
          x: x + f * (SPREAD_X / 2),
          y,
          vx: f * SPREAD_VX,
          vy: -40,
          targetX: x,
          targetY: y,
          ownerId: worm.id,
          loiter: true,
          precise: true,
          track: false, // camera stays on the bomb/tracer, not these
        });
        loiterers.push(missile);
        world.addEntity(missile as never);
      }
    };

    // When the bomb finally hits, release the loitering missiles onto the
    // exact contact point so they all converge there.
    bomb.onExplode = (x, y) => {
      for (const m of loiterers) {
        if (m.active) m.release(x, y);
      }
    };

    return { success: true, entities: [bomb] };
  },
};
